import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  HelpCircle,
  BookOpen,
  DollarSign,
  Settings,
  UserCircle,
  Crown,
  ListChecks,
  ChevronDown,
  ChevronLeft,
  Lock,
  ShieldAlert,
  Zap,
} from "lucide-react";
import { useLicenseStatus } from "@/hooks/use-license";
import logoLight from "@/assets/logo-light.png";
import logoDark from "@/assets/logo-dark.png";
import logoIconLight from "@/assets/logo-icon-light.png";
import logoIconDark from "@/assets/logo-icon-dark.png";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { useUIStore } from "@/stores/ui.store";
import { useAuthStore } from "@/stores/auth.store";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { Badge } from "@/components/ui/badge";
import { useCommissions } from "@/hooks/use-commissions";
import { useRules } from "@/hooks/use-rules";
import { useSettings } from "@/hooks/use-settings";
import { useTokenHealth } from "@/hooks/use-token-health";
import { CURRENT_STORAGE_SCHEMA_VERSION } from "@shared";
import type { FeatureGate } from "@shared";

// ═══════════════════════════════════════════════════════════
// Sidebar — Navigation tabs (TPP §5.1)
// ═══════════════════════════════════════════════════════════

interface NavSubItem {
  id: string;
  label: string;
  path: string;
  isStatusCount?: boolean;
}

interface NavItem {
  id: string;
  labelKey: string;
  path: string;
  icon: React.ElementType;
  subItems?: NavSubItem[];
  /** When set, item is visible but locked (upgrade nudge) if the feature is disabled */
  featureGated?: keyof FeatureGate;
}

const coreNavItemsTop: NavItem[] = [
  { id: "dashboard", labelKey: "nav.dashboard", path: "/dashboard", icon: LayoutDashboard },
  {
    id: "rules",
    labelKey: "nav.rules",
    path: "/rules",
    icon: BookOpen,
    subItems: [
      { id: "rules_list", label: "Rules List", path: "/rules" },
      { id: "rules_templates", label: "Templates", path: "/rules/templates" },
    ],
  },
  {
    id: "commissions",
    labelKey: "nav.commissions",
    path: "/commissions",
    icon: DollarSign,
    subItems: [
      { id: "commissions_all", label: "All Entries", path: "/commissions" },
      {
        id: "commissions_pending",
        label: "Pending Approval",
        path: "/commissions?status=CALCULATED",
        isStatusCount: true,
      },
      { id: "commissions_approved", label: "Approved", path: "/commissions?status=APPROVED" },
      { id: "commissions_paid", label: "Paid", path: "/commissions?status=PAID" },
    ],
  },
];

const adminEventsItem: NavItem = {
  id: "events",
  labelKey: "nav.eventLog",
  path: "/events",
  icon: ListChecks,
  featureGated: "eventLogEnabled",
};

const coreHelpItem: NavItem = { id: "help", labelKey: "nav.help", path: "/help", icon: HelpCircle };

const adminSettingsItem: NavItem = {
  id: "settings",
  labelKey: "nav.settings",
  path: "/settings",
  icon: Settings,
  subItems: [
    { id: "settings_general", label: "General", path: "/settings?tab=general" },
    { id: "settings_system", label: "System", path: "/settings?tab=system" },
    { id: "settings_workflows", label: "Workflows", path: "/settings?tab=workflows" },
    { id: "settings_webhooks", label: "Webhooks", path: "/settings?tab=webhooks" },
    { id: "settings_currencies", label: "Currencies", path: "/settings?tab=currencies" },
    { id: "settings_data", label: "Data Management", path: "/settings?tab=data" },
    { id: "settings_license", label: "License & Billing", path: "/settings?tab=license" },
    { id: "settings_integrations", label: "Integrations", path: "/settings?tab=integrations" },
  ],
};

// IDs that remain accessible even when license is expired
const ALWAYS_UNLOCKED = new Set(["settings", "help"]);

export function Sidebar() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const currentUser = useAuthStore((s) => s.currentUser);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const location = useLocation();

  // License status for nav gating (uses cached value — no extra API call)
  const { data: license } = useLicenseStatus();
  const licenseStatus = license?.status;
  const isLicenseExpired = licenseStatus === "EXPIRED" || licenseStatus === "CANCELLED";
  const isBlocked = licenseStatus === "BLOCKED";

  // Fetch pending count for the "Pending Approval" sub item
  const { data: pendingData } = useCommissions({ status: "CALCULATED", pageSize: 1 });
  const pendingCount = pendingData?.total ?? 0;

  // Active rules count — minimal payload, 2-min cache (rules change rarely)
  const { data: rulesData } = useRules({ activeOnly: true, pageSize: 1 }, { staleTime: 120_000 });
  const activeRulesCount = rulesData?.total ?? 0;

  // System alerts — derived from already-fetched settings (zero extra API calls)
  const { data: settings } = useSettings();
  const { data: tokenHealth } = useTokenHealth();
  const systemAlertCount =
    (settings?.pendingTaskIds?.length ?? 0) +
    (settings?.storageSchemaVersion != null &&
    settings.storageSchemaVersion !== CURRENT_STORAGE_SCHEMA_VERSION
      ? 1
      : 0) +
    (tokenHealth?.tokenStatus === "REVOKED" || tokenHealth?.tokenStatus === "REFRESH_FAILED"
      ? 1
      : 0);

  const navItems = [
    ...coreNavItemsTop,
    ...(isAdmin ? [adminEventsItem] : []),
    coreHelpItem,
    ...(isAdmin ? [adminSettingsItem] : []),
  ];

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    commissions: true,
    settings: true,
  });

  // Auto-expand based on active path
  useEffect(() => {
    const currentPath = location.pathname;
    const newExpanded = { ...expandedItems };
    let changed = false;

    for (const item of navItems) {
      if (item.subItems && currentPath.startsWith(item.path)) {
        if (!newExpanded[item.id]) {
          newExpanded[item.id] = true;
          changed = true;
        }
      }
    }

    if (changed) setExpandedItems(newExpanded);
  }, [location.pathname, navItems]);

  const initials = currentUser
    ? currentUser.fullName
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0] ?? "")
        .join("")
        .toUpperCase()
    : "";

  const avatarNode = (
    <div
      className={cn(
        "flex items-center gap-3 min-w-0 px-2 py-1.5 rounded-lg transition-colors group/avatar",
        sidebarOpen && "hover:bg-primary/5 cursor-pointer",
      )}
    >
      <div className="relative shrink-0 h-10 w-10">
        <div className="h-10 w-10 rounded-full overflow-hidden bg-primary/10 ring-2 ring-inset ring-primary/5 group-hover/avatar:ring-primary/20 flex items-center justify-center transition-all">
          {currentUser?.photo ? (
            <img
              src={currentUser.photo}
              alt={currentUser.fullName}
              className="h-full w-full object-cover"
            />
          ) : currentUser ? (
            <span className="text-sm font-bold text-primary select-none">{initials}</span>
          ) : (
            <UserCircle className="h-6 w-6 text-primary/70" />
          )}
        </div>
        {isAdmin && (
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-amber-500 ring-2 ring-white dark:ring-slate-900 flex items-center justify-center shadow-sm">
            <Crown className="h-2.5 w-2.5 text-white dark:text-slate-900" />
          </span>
        )}
      </div>
      {sidebarOpen && (
        <div className="min-w-0 flex flex-col leading-tight">
          <span className="text-[14px] font-semibold text-foreground truncate group-hover/avatar:text-primary transition-colors">
            {currentUser?.fullName ?? "—"}
          </span>
          {currentUser?.workPosition && (
            <span className="text-[11px] text-muted-foreground truncate uppercase tracking-tight font-medium">
              {currentUser.workPosition}
            </span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "sidebar-b24 flex flex-col border-r transition-all duration-200",
          sidebarOpen ? "w-56" : "w-14",
        )}
      >
        <div
          className={cn("flex h-14 items-center px-3 gap-2", !sidebarOpen && "justify-center px-0")}
        >
          {sidebarOpen ? (
            <>
              <div className="flex-1 min-w-0 flex items-center pl-1">
                <img
                  src={logoLight}
                  alt="BitCommissions24"
                  className="block dark:hidden h-10 w-auto max-w-[168px]"
                />
                <img
                  src={logoDark}
                  alt="BitCommissions24"
                  className="hidden dark:block h-10 w-auto max-w-[168px]"
                />
              </div>
              <button
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-secondary hover:bg-primary/10 hover:text-primary transition-colors"
                onClick={toggleSidebar}
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </>
          ) : (
            <button
              className="flex items-center justify-center h-9 w-9 rounded-md hover:bg-primary/10 transition-colors"
              onClick={toggleSidebar}
              aria-label="Expand sidebar"
            >
              <img
                src={logoIconLight}
                alt="BitCommissions24"
                className="block dark:hidden h-8 w-8 object-contain"
              />
              <img
                src={logoIconDark}
                alt="BitCommissions24"
                className="hidden dark:block h-8 w-8 object-contain"
              />
            </button>
          )}
        </div>

        <div className="sidebar-separator h-px w-full" />

        <ScrollArea className="flex-1 py-3">
          <nav className="flex flex-col gap-0.5 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const hasSubItems = !!item.subItems?.length;
              const isExpanded = expandedItems[item.id];
              const isRouteActive = location.pathname.startsWith(item.path);

              // ── Per-item badge ──────────────────────────────────────
              // ── License lock (expired or blocked) ────────────────────────
              const isNavLocked =
                (isLicenseExpired && !ALWAYS_UNLOCKED.has(item.id)) ||
                (isBlocked && item.id !== "settings");
              // ── Feature lock (plan doesn't include this feature) ──────
              const isFeatureLocked =
                !isNavLocked &&
                !!item.featureGated &&
                license?.features?.[item.featureGated] === false;

              const itemBadge: {
                count: number;
                variant: "default" | "destructive" | "warning";
              } | null =
                item.id === "rules" && activeRulesCount > 0
                  ? { count: activeRulesCount, variant: "default" }
                  : item.id === "commissions" && pendingCount > 0
                    ? { count: pendingCount, variant: "destructive" }
                    : item.id === "settings" && systemAlertCount > 0
                      ? { count: systemAlertCount, variant: "warning" }
                      : null;

              const badgeNode = itemBadge ? (
                <span
                  className={cn(
                    "inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold leading-none shrink-0",
                    itemBadge.variant === "default" && "bg-primary/15 text-primary",
                    itemBadge.variant === "destructive" &&
                      "bg-destructive text-destructive-foreground",
                    itemBadge.variant === "warning" &&
                      "bg-amber-500/20 text-amber-700 dark:text-amber-400",
                  )}
                >
                  {itemBadge.count > 99 ? "99+" : itemBadge.count}
                </span>
              ) : null;

              const linkContent = (
                <>
                  <Icon
                    className={cn(
                      "nav-icon h-4 w-4 shrink-0 transition-colors",
                      isNavLocked || isFeatureLocked
                        ? "text-muted-foreground/50"
                        : isRouteActive && !hasSubItems
                          ? "text-white"
                          : "text-secondary group-hover:text-foreground",
                      isRouteActive &&
                        hasSubItems &&
                        !isNavLocked &&
                        !isFeatureLocked &&
                        "text-primary",
                    )}
                  />
                  {sidebarOpen && (
                    <span
                      className={cn(
                        "flex-1 text-left truncate leading-tight",
                        (isNavLocked || isFeatureLocked) && "text-muted-foreground/50",
                      )}
                    >
                      {t(item.labelKey)}
                    </span>
                  )}
                  {sidebarOpen && !isNavLocked && !isFeatureLocked && badgeNode}
                  {sidebarOpen && (isNavLocked || isFeatureLocked) && (
                    <Lock className="h-3 w-3 shrink-0 text-muted-foreground/40" />
                  )}
                  {sidebarOpen && !isNavLocked && !isFeatureLocked && hasSubItems && (
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 transition-transform duration-200",
                        isRouteActive && hasSubItems ? "text-primary" : "text-muted-foreground",
                        isExpanded && "rotate-180",
                      )}
                    />
                  )}
                </>
              );

              // Locked nav item — always render as button that opens billing
              if (isNavLocked) {
                const lockedNode = (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      navigate("/settings?tab=license");
                    }}
                    className={cn(
                      "sidebar-nav-link group w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors opacity-50 cursor-not-allowed",
                      !sidebarOpen && "justify-center px-0",
                    )}
                  >
                    {linkContent}
                  </button>
                );
                return (
                  <div key={item.id} className="flex flex-col mb-1">
                    {!sidebarOpen ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{lockedNode}</TooltipTrigger>
                        <TooltipContent side="right">
                          <span className="flex items-center gap-1.5">
                            <ShieldAlert className="size-3 text-destructive" />
                            {t("license.navLockedTooltip")}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      lockedNode
                    )}
                  </div>
                );
              }

              // Feature-locked item — visible but greyed out, clicking opens billing
              if (isFeatureLocked) {
                const featureLockedNode = (
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/settings?tab=license");
                    }}
                    className={cn(
                      "sidebar-nav-link group w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors opacity-50 hover:opacity-70",
                      !sidebarOpen && "justify-center px-0",
                    )}
                  >
                    {linkContent}
                  </button>
                );
                return (
                  <div key={item.id} className="flex flex-col mb-1">
                    <Tooltip>
                      <TooltipTrigger asChild>{featureLockedNode}</TooltipTrigger>
                      <TooltipContent side="right" className="flex items-center gap-1.5">
                        <Zap className="size-3 text-primary" />
                        {t("featureGate.navUpgradeTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                );
              }

              const navLinkNode = hasSubItems ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (!sidebarOpen) setSidebarOpen(true);
                    setExpandedItems((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                  }}
                  className={cn(
                    "sidebar-nav-link group w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors",
                    isRouteActive && "active-parent",
                    !isRouteActive && "text-secondary hover:text-foreground",
                    !sidebarOpen && "justify-center px-0",
                  )}
                >
                  {linkContent}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  className={cn(
                    "sidebar-nav-link group flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium transition-colors",
                    isRouteActive && "bg-primary text-primary-foreground hover:bg-primary/90",
                    !isRouteActive && "text-secondary hover:text-foreground",
                    !sidebarOpen && "justify-center px-0",
                  )}
                >
                  {linkContent}
                </Link>
              );

              return (
                <div key={item.id} className="flex flex-col mb-1">
                  {!sidebarOpen ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{navLinkNode}</TooltipTrigger>
                      <TooltipContent side="right" className="flex items-center gap-2">
                        {t(item.labelKey)}
                        {itemBadge && (
                          <span
                            className={cn(
                              "inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold leading-none",
                              itemBadge.variant === "default" && "bg-primary/20 text-primary",
                              itemBadge.variant === "destructive" &&
                                "bg-destructive text-destructive-foreground",
                              itemBadge.variant === "warning" && "bg-amber-500/20 text-amber-700",
                            )}
                          >
                            {itemBadge.count > 99 ? "99+" : itemBadge.count}
                          </span>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    navLinkNode
                  )}

                  {/* Collapsed tooltip shows badge count too */}

                  {/* Render SubMenu */}
                  {sidebarOpen && hasSubItems && isExpanded && (
                    <div className="mt-1 ml-4 pl-3 border-l border-border/50 flex flex-col gap-0.5">
                      {item.subItems!.map((sub) => {
                        // Very rough query match or literal path check
                        const isSubActive = location.pathname + location.search === sub.path;
                        return (
                          <Link
                            key={sub.id}
                            to={sub.path}
                            className={cn(
                              "flex items-center justify-between rounded-md px-3 py-1.5 text-xs text-secondary transition-colors",
                              isSubActive
                                ? "text-primary font-medium bg-primary/5"
                                : "hover:text-foreground hover:bg-secondary/5",
                            )}
                          >
                            <span className="truncate">{sub.label}</span>
                            {sub.isStatusCount && pendingCount > 0 && (
                              <Badge
                                variant="destructive"
                                className="h-4 px-1 min-w-4 text-[10px] leading-none shrink-0 flex items-center justify-center"
                              >
                                {pendingCount > 99 ? "99+" : pendingCount}
                              </Badge>
                            )}
                            {sub.id === "settings_system" && systemAlertCount > 0 && (
                              <span className="inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold leading-none bg-amber-500/20 text-amber-700 dark:text-amber-400 shrink-0">
                                {systemAlertCount}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="sidebar-separator h-px w-full" />

        {/* ── Plan status pill ────────────────────────────────────── */}
        {sidebarOpen && licenseStatus && (
          <button
            type="button"
            onClick={() => {
              navigate("/settings?tab=license");
            }}
            className={cn(
              "mx-2 mb-1 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors",
              isLicenseExpired
                ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
                : licenseStatus === "TRIAL" || licenseStatus === "GRACE_PERIOD"
                  ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20"
                  : "bg-primary/8 text-primary hover:bg-primary/15",
            )}
          >
            {isLicenseExpired ? (
              <ShieldAlert className="size-3 shrink-0" />
            ) : licenseStatus === "ACTIVE" ? (
              <Zap className="size-3 shrink-0" />
            ) : (
              <ShieldAlert className="size-3 shrink-0" />
            )}
            <span className="truncate">
              {license?.planSlug === "enterprise"
                ? t("license.planEnterprise")
                : license?.planSlug === "sales_booster"
                  ? t("license.planSalesBooster")
                  : license?.planSlug === "free"
                    ? "Free Plan"
                    : licenseStatus === "TRIAL"
                      ? t("license.planTrial")
                      : licenseStatus === "GRACE_PERIOD"
                        ? t("license.planGrace")
                        : t("license.planExpired")}
            </span>
          </button>
        )}

        <div className={cn("px-2 py-3 flex flex-col gap-1", !sidebarOpen && "items-center px-0")}>
          <LanguageSwitcher collapsed={!sidebarOpen} />
          <ThemeSwitcher collapsed={!sidebarOpen} />
        </div>

        <div
          className={cn(
            "px-2 py-4 border-t border-border/50",
            !sidebarOpen && "flex justify-center",
          )}
        >
          {!sidebarOpen ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer">{avatarNode}</div>
              </TooltipTrigger>
              <TooltipContent side="right" className="flex flex-col gap-0.5">
                <p className="font-semibold text-sm">{currentUser?.fullName ?? "—"}</p>
                {currentUser?.workPosition && (
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">
                    {currentUser.workPosition}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          ) : (
            avatarNode
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
